import * as _ from 'underscore';
import { ILockUnlockDatabasePermissionsScope } from '../directives/tbLockUnlockDatabasePermissions';
import { CommonErrorCode } from '../helpers/ErrorCodes';
import { LockUnlockPermissionsState } from '../helpers/LockStatusHelper';
import { L10n } from '../L10n/L10n';
import { ILongRunningAction } from '../models/ILongRunningAction';
import { IControlledPermissionItemInfo } from '../models/resourceInfoParts';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { Databases } from '../resources/Databases';
import { IActionResult } from '../ServerApi/types/IActionResult';
import { IError } from '../ServerApi/types/IError';
import { IEmptyResult, IResult } from '../ServerApi/types/IResponse';
import { LongRunningActionService } from './LongRunningActionService';
import { ErrorReason } from './ServerService';
import { IToastOptions, ToasterService, ToastType } from './ToasterService';

export class SetDatabaseControlledPermissionsAction {

  public static $inject = ['$translate', 'Databases', 'LongRunningActionService'];

  private toaster = ToasterService.instance;

  constructor(
    private $translate: ng.translate.ITranslateService,
    private Databases: Databases,
    private longRunningActionService: LongRunningActionService,
    private scope: ILockUnlockDatabasePermissionsScope) { }

  public execute(databases: Set<IControlledPermissionItemInfo>, updatedPermissionSource: LockUnlockPermissionsState[],
                 scope: ILockUnlockDatabasePermissionsScope): ng.IPromise<IResult> {

    this.scope = scope;
    if (_.first(updatedPermissionSource) === LockUnlockPermissionsState.Locked) {
      return this.lockDatabasePermissions(databases);
    } else {
      return this.unlockDatabasePermissions(databases);
    }
  }

  private lockDatabasePermissions(databases: Set<IControlledPermissionItemInfo>): ng.IPromise<IResult> {
    const dbIds: string[] = databases.pluck('id');

    return this.Databases.enableDatabaseControlledPermissions(dbIds)
      .then((result: IEmptyResult) => {
        let toast: IToastOptions;
        if (result.errors && result.errors.length) {
          toast = {
            type: ToastType.Error,
            text: this.errorMessageFor(result.errors[0], databases.first()),
          };
        } else {
          const permission_source: string = this.$translate.instant('lockDatabasePermissionsChangeSuccess_locked',
            { DATABASE_COUNT : databases.size() });
          toast = {
            type: ToastType.Info,
            text: this.$translate.instant('lockDatabasePermissionsChangeSuccess',
              { DATABASE_COUNT: databases.size(),
                DATABASE_NAME: L10n.elideIfLong(databases.first().name),
                PERMISSION_SOURCE: permission_source
              }, L10n.Interpolations.Count),
          };
        }
        this.toaster.pop(toast);
        return result;
      }
    );
  }

  private unlockDatabasePermissions(databases: Set<IControlledPermissionItemInfo>): ng.IPromise<IResult> {
    const dbIds: string[] = databases.pluck('id');

    const permissionChangeSuccess = (result: ILongRunningAction) => {
      if (result && result.succeeded === false) {
        permissionsChangeFail(ErrorReason.Unknown);
      } else {
        this.scope.percentComplete = result.percentComplete;
        const permission_source: string = this.$translate.instant('lockDatabasePermissionsChangeSuccess_unlocked',
          { DATABASE_COUNT : databases.size() });
        const toast: IToastOptions = {
          type: ToastType.Info,
          text: this.$translate.instant('lockDatabasePermissionsChangeSuccess',
            { DATABASE_COUNT: databases.size(),
              DATABASE_NAME: L10n.elideIfLong(databases.first().name),
              PERMISSION_SOURCE: permission_source
            }, L10n.Interpolations.Count),
        };
        this.toaster.pop(toast);
        this.scope.busyUnlocking = false;
      }

      return result.response;
    };

    const permissionsChangeFail = (reason: ErrorReason) => {
      this.scope.busyUnlocking = false;
      const toast: IToastOptions = {
        type: ToastType.Error,
        text: this.$translate.instant('errorUnknown'),
      };
      this.toaster.pop(toast);
    };

    const notifyProgress = (action: ILongRunningAction) => {
      this.scope.busyUnlocking = true;
      this.scope.percentComplete = action.percentComplete;
    };

    return this.Databases.disableDatabaseControlledPermissions(dbIds)
      .then((result: IActionResult) => {
        const progress = this.longRunningActionService.trackAsyncJobProgress(result.actionId);
        return progress.promise.then(permissionChangeSuccess, permissionsChangeFail, notifyProgress);
      }
    );
  }

  private errorMessageFor(error: IError, database: IControlledPermissionItemInfo) {
    switch (error.code) {
      case CommonErrorCode.DatabaseNotFound:
        return this.$translate.instant('errorDatabaseNotFound', { DATABASE_NAME: L10n.elideIfLong(database.name) });
      default:
        return this.$translate.instant('errorUnknown');
    }
  }
}

Module.instance().service('SetDatabaseControlledPermissionsAction',
    SetDatabaseControlledPermissionsAction);
